// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { actions as offerReduxActionTypes } from '@makemydeal/dr-offer-redux';

// action types
import { APPLY_LEASE_CONTRACT_FIELDS } from '../../actionTypes/leaseContractingFieldsActionTypes';

// selectors
import * as offerReduxSelectors from '../../selectors/offerRedux';

// Shared by both endpoint middlewares, which are mutually exclusive by feature toggle - a rule written in only
// one stops applying the moment `isImprovedAutoSelectEnabled` flips. Both callers pass the pre-`next()` state,
// so the two verdicts cannot disagree about the action they are describing.

const PQ_SUPPORTED_OVERRIDE_ACTIONS: ReadonlySet<string> = new Set([
    offerReduxActionTypes.UPDATED_SELL_RATE_OVERRIDE,
    // Residual travels in both units on every request, so program-quotes prices it in either unit.
    offerReduxActionTypes.UPDATED_BASE_RESIDUAL_DOLLAR_OVERRIDE,
    offerReduxActionTypes.UPDATED_BASE_RESIDUAL_PERCENT_OVERRIDE,
    offerReduxActionTypes.UPDATED_PROGRAM_RESIDUAL_DOLLAR_OVERRIDE,
    offerReduxActionTypes.UPDATED_PROGRAM_RESIDUAL_PERCENT_OVERRIDE
]);

/** Writes no override, so it must not move the engine; deliberately not subject to `hasProgramOnDeal`. */
const ENDPOINT_NEUTRAL_ACTIONS: ReadonlySet<string> = new Set([offerReduxActionTypes.UPDATE_RESIDUAL_TAB_DISPLAY_TYPE]);

/**
 * Mileage & Cost Options fields only `/user-program-quotes` can price, named as `changedFields` reports them.
 *
 * Two omissions are deliberate. `annualMiles` rides the lease offer node, not `overrides`, and program-quotes
 * reads it to pick the mileage tier's program. `milesAtInception` is editable in the same slideout but never
 * reaches the payload at all - it is written to `leaseContractingFields.fields` while the component reads it
 * back from `vehicleDetails?.mileage` - so no endpoint can price it and moving the deal for it would be a
 * switch with no payload behind it.
 */
const UPQ_ONLY_MILEAGE_FIELDS: ReadonlySet<string> = new Set([
    'standardAllowedMiles',
    'excessMileRate',
    'thresholdMiles',
    'inceptionRate',
    'retroMiles',
    'mileagePenaltyRate'
]);

const changedFieldsAreUpqOnly = (changedFields: unknown): boolean =>
    Array.isArray(changedFields) && changedFields.some((field) => UPQ_ONLY_MILEAGE_FIELDS.has(field as string));

const isAspPgRedesignOn = (state: StateTree, offerType: OfferType): boolean =>
    offerReduxSelectors.isAspPaymentGridRedesignActive(state, offerType);

const isExplicitLenderP1On = (state: StateTree): boolean =>
    featureToggleSelectors.areFeatureTogglesAvailable(state) && featureToggleSelectors.isExplicitLenderP1Enabled(state);

const isBuyRateOverrideOnPq = (state: StateTree, action: AnyFSA): boolean =>
    action.type === offerReduxActionTypes.UPDATED_BUY_RATE_OVERRIDE &&
    featureToggleSelectors.areFeatureTogglesAvailable(state) &&
    featureToggleSelectors.isBuyRatePqFirstEnabled(state);

const isPqSupportedOverride = (state: StateTree, action: AnyFSA): boolean =>
    PQ_SUPPORTED_OVERRIDE_ACTIONS.has(action.type) || isBuyRateOverrideOnPq(state, action);

/** The same test `handlePqFirstFallback` and the program-lookup outcome use, so all three cannot disagree. */
export const hasProgramOnDeal = (state: StateTree, offerType: OfferType): boolean =>
    Boolean(offerReduxSelectors.getSelectedTermByOfferType(state, offerType)?.lenderProgram?.programName);

/**
 * A program on the deal is required, not incidental: program-quotes builds `selectedProgramOverrides` - the
 * only node carrying `sellRateValue` - inside `if (keyProgram)`, so with no program it ignores the rate
 * outright. Keeping such a deal on program-quotes would drop the very override this rule exists to deliver,
 * so it falls back to user-program-quotes as before.
 *
 * Reachable, not defensive: a deal can load on program-quotes before any program lookup, from Acceleron's
 * endpoint, from `US2161518_Bypass_Dealer_Auto_Select_Setting` or, with it off, from the dealer's
 * `autoSelectProgramByDefault` (`transformAcceleronResponse`), and a load payment that finds no program is not
 * recovered - that path is gated on `PROGRAM_QUOTES_REFETCH_META`, which only an explicit re-fetch carries.
 */
export const overrideKeepsCalculationEndpoint = (state: StateTree, action: AnyFSA): boolean => {
    // On its own gate: `F298789_ASP_PG_redesign` keeps this toggle switching to user-program-quotes
    // (US2067816), and only under P1 does the tab stop asking for a payment behind it.
    if (ENDPOINT_NEUTRAL_ACTIONS.has(action.type)) return isExplicitLenderP1On(state);

    if (!isPqSupportedOverride(state, action)) return false;

    const offerType = offerReduxSelectors.getCurrentOfferType(state);
    return isAspPgRedesignOn(state, offerType) && hasProgramOnDeal(state, offerType);
};

export const mileageOverrideNeedsUserProgramQuotes = (state: StateTree, action: AnyFSA): boolean =>
    action.type === APPLY_LEASE_CONTRACT_FIELDS &&
    changedFieldsAreUpqOnly(action.meta?.changedFields) &&
    isAspPgRedesignOn(state, offerReduxSelectors.getCurrentOfferType(state));
